/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import { ColorProps, } from "@components/types";

type Theme = {
  palette?: Palette;
}

type Palette = {
  background?: { default?: string };
  primary?: {
    main?: string;
    contrastText?: string;
  };
  secondary?: {
    main?: string;
    contrastText?: string;
  };
  text?: {
    primary?: string;
    secondary?: string;
  };
};

// Defaults do Material-UI v4 (light) — alinhados com createPalette do @material-ui/core.
export const DEFAULT_PALETTE = {
  background: { default: "#fafafa" },
  primary: {
    main: "#3f51b5",
    contrastText: "#ffffff",
  },
  secondary: {
    main: "#f50057",
    contrastText: "#ffffff",
  },
  text: {
    primary: "#000000de",
    secondary: "#0000008a",
  },
};

export default function useTheme(){
  function setTheme(theme?: Theme){
    const palette = theme?.palette;

    setPrimaryColor(palette?.primary?.main ?? DEFAULT_PALETTE.primary.main);
    setSecondaryColor(palette?.secondary?.main ?? DEFAULT_PALETTE.secondary.main);
    setBackgroundColor(palette?.background?.default ?? DEFAULT_PALETTE.background.default);
    setTextPrimaryColor(palette?.text?.primary ?? DEFAULT_PALETTE.text.primary);
    setTextSecondaryColor(palette?.text?.secondary ?? DEFAULT_PALETTE.text.secondary);
    setTextContrastPrimaryColor(palette?.primary?.contrastText ?? DEFAULT_PALETTE.primary.contrastText);
    setTextContrastSecondaryColor(palette?.secondary?.contrastText ?? DEFAULT_PALETTE.secondary.contrastText);
  }

  function setPrimaryColor(color: string){
    document.documentElement.style.setProperty("--primary-color", color);
  }

  function setSecondaryColor(color: string){
    document.documentElement.style.setProperty("--secondary-color", color);
  }

  function setBackgroundColor(color: string){
    document.documentElement.style.setProperty("--background-color", color);
  }

  function setTextPrimaryColor(color: string){
    document.documentElement.style.setProperty("--text-color-primary", color);
  }

  function setTextSecondaryColor(color: string){
    document.documentElement.style.setProperty("--text-color-secondary", color);
  }

  function setTextContrastPrimaryColor(color: string){
    document.documentElement.style.setProperty("--text-contrast-primary-color", color);
  }

  function setTextContrastSecondaryColor(color: string){
    document.documentElement.style.setProperty("--text-contrast-secondary-color", color);
  }

  function getPrimaryColor(){
    return getComputedStyle(document.documentElement).getPropertyValue("--primary-color").trim() || DEFAULT_PALETTE.primary.main;
  }

  function getSecondaryColor(){
    return getComputedStyle(document.documentElement).getPropertyValue("--secondary-color").trim() || DEFAULT_PALETTE.secondary.main;
  }

  function getBackgroundColor(){
    return getComputedStyle(document.documentElement).getPropertyValue("--background-color").trim() || DEFAULT_PALETTE.background.default;
  }

  function getTextPrimaryColor(){
    return getComputedStyle(document.documentElement).getPropertyValue("--text-color-primary").trim() || DEFAULT_PALETTE.text.primary;
  }

  function getTextSecondaryColor(){
    return getComputedStyle(document.documentElement).getPropertyValue("--text-color-secondary").trim() || DEFAULT_PALETTE.text.secondary;
  }

  function getTextContrastPrimaryColor(){
    return getComputedStyle(document.documentElement).getPropertyValue("--text-contrast-primary-color").trim() || DEFAULT_PALETTE.primary.contrastText;
  }

  function getTextContrastSecondaryColor(){
    return getComputedStyle(document.documentElement).getPropertyValue("--text-contrast-secondary-color").trim() || DEFAULT_PALETTE.secondary.contrastText;
  }

  function getColor(color: ColorProps){
    switch(color){
    case "primary":
      return getPrimaryColor();
    case "secondary":
      return getSecondaryColor();
    default:
      return "";
    }
  }

  return {
    setTheme,
    setPrimaryColor,
    setSecondaryColor,
    setBackgroundColor,
    setTextPrimaryColor,
    setTextSecondaryColor,
    setTextContrastPrimaryColor,
    setTextContrastSecondaryColor,

    getPrimaryColor,
    getSecondaryColor,
    getBackgroundColor,
    getTextPrimaryColor,
    getTextSecondaryColor,
    getTextContrastPrimaryColor,
    getTextContrastSecondaryColor,

    getColor
  };
}
